import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, Modal, TextField, MenuItem, Alert, Switch, FormControlLabel, Chip} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {adminApi} from '../../../../services'
import {parseError} from '../../../../utils/helpers'

// How HHAeXchange identifies the office a visit belongs to. Which one applies is
// whatever HHAX has on file for the agency — their EDI export shows it — not a
// preference.
export const QUALIFIERS = [
    {value: 'FederalTaxID', label: 'Federal Tax ID'},
    {value: 'NPI', label: 'NPI'},
    {value: 'UMPI', label: 'UMPI'},
]

export default ({open, setOpen, organization, branch, onSaved}) => {
    const {setLoading, setMessageBox} = useGlobalStates()

    const [enabled, setEnabled] = useState(false)
    const [qualifier, setQualifier] = useState('FederalTaxID')
    const [identifier, setIdentifier] = useState('')
    const [problem, setProblem] = useState(null)

    useEffect(() => {
        if (!open) return
        setEnabled(Boolean(branch?.hhax?.enabled))
        setQualifier(branch?.hhax?.office_qualifier || 'FederalTaxID')
        setIdentifier(branch?.hhax?.office_identifier || '')
        setProblem(null)
    }, [open])

    // Offered, never applied: the identifier HHAX expects is not always the
    // number Careviah already holds, and the wrong one fails with 101007 or
    // 101009 — errors that read like a typo rather than a wrong choice.
    const suggestion =
        qualifier === 'FederalTaxID'
            ? organization?.federal_tax_id && {label: "Organization's EIN", value: organization.federal_tax_id}
            : qualifier === 'NPI'
              ? branch?.provider_number && {label: "Branch's NPI", value: branch.provider_number}
              : null
    const showSuggestion = suggestion && suggestion.value !== identifier.trim()

    // Enrolling is allowed before the rest is ready, but its visits will not go
    // anywhere until it is — so say so here rather than after they pile up.
    const credential = organization?.hhax?.credential
    const missing = [!credential?.configured && 'HHAeXchange credentials', !organization?.federal_tax_id && 'a Federal Tax ID'].filter(Boolean)

    const save = () => {
        if (enabled && !identifier.trim()) {
            setProblem('An enrolled branch needs an office identifier.')
            return
        }
        setProblem(null)
        setLoading(true)
        adminApi
            .updateBranch(organization?.id, branch?.id, {
                hhax: {enabled, office_qualifier: qualifier, office_identifier: identifier.trim()},
            })
            .then(() => {
                setLoading(false)
                setOpen(false)
                setMessageBox({
                    status: 'success',
                    title: enabled ? 'Branch enrolled' : 'Branch updated',
                    description: enabled
                        ? `Visits at ${branch?.branch_name} that require EVV will be reported to HHAeXchange.`
                        : `Visits at ${branch?.branch_name} will not be reported to HHAeXchange.`,
                })
                onSaved?.()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (error?.response?.status === 400) {
                    setProblem(description || title)
                    return
                }
                setOpen(false)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not update branch',
                    description: description || 'There was an error saving this branch. Please try again later.',
                })
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            EVV for {branch?.branch_name}
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary">
                            HHAeXchange tells this branch apart from the organization's others by its office identifier.
                        </Typography>
                    </div>
                    <br />
                    {problem && (
                        <>
                            <Alert severity="error">{problem}</Alert>
                            <br />
                        </>
                    )}
                    <div className="field">
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />}
                            label="Report this branch's visits to HHAeXchange"
                        />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Office Qualifier
                        </Typography>
                        <TextField select fullWidth value={qualifier} onChange={e => setQualifier(e.target.value)}>
                            {QUALIFIERS.map(q => (
                                <MenuItem key={q.value} value={q.value}>
                                    {q.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Office Identifier
                        </Typography>
                        <TextField
                            placeholder="As HHAeXchange has it on file"
                            fullWidth
                            value={identifier}
                            onChange={e => setIdentifier(e.target.value)}
                        />
                        {showSuggestion && (
                            <div className="suggestion">
                                <Typography variant="body2" color="text.secondary">
                                    {suggestion.label}:
                                </Typography>
                                <Chip
                                    label={`Use ${suggestion.value}`}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setIdentifier(suggestion.value)}
                                />
                            </div>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Must match HHAeXchange exactly. Check it against their export for this agency.
                        </Typography>
                    </div>
                    {enabled && missing.length > 0 && (
                        <>
                            <Alert severity="warning">Visits will not reach HHAeXchange until this organization has {missing.join(' and ')}.</Alert>
                            <br />
                        </>
                    )}
                    <div className="actions">
                        <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth onClick={save}>
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
